import {FormulaValueCoercionUtils} from '@mintdata-wsp/common/controllers/spreadsheet/formula/FormulaValueCoercionUtils';
import {zipcelx} from '@mintdata-wsp/common/controllers/spreadsheet/functions/util/zipcelx/zipcelx';
import {IFunctionContainer} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/ClientFunction';
import {Function, Param} from '@mintdata/mint-kit/api/decorators/Function';
import {ISheetAPI, SheetType, SheetValueType, SimpleNamingContext} from '@mintdata/mint-kit/api/functions/SheetAPI';
import {Type} from '@mintdata/mint-kit/api/types/Type';
import {IValue} from '@mintdata/mint-kit/api/types/Value';
import {ABoolean} from '@mintdata/mint-kit/api/values/ABoolean';
import {AError} from '@mintdata/mint-kit/api/values/AError';
import {ANumber} from '@mintdata/mint-kit/api/values/ANumber';
import {AString} from '@mintdata/mint-kit/api/values/AString';
import {AFrame} from '@mintdata/mint-kit/api/values/frames/AFrame';
import {IFrame, IFrameField} from '@mintdata/mint-kit/api/values/frames/Frame';
import bind from 'bind-decorator';
const FileSaver = require('file-saver');

interface IExportCell
{
    readonly value:number | string;
    readonly type:string;
}

export class ExportFunctions implements IFunctionContainer
{
    public get namespace()
    {
        return 'export';
    }

    @Function({
        title: 'DOWNLOAD_AS_EXCEL(filename, frame)',
        alias: 'DOWNLOAD_AS_EXCEL',
        example: 'DOWNLOAD_AS_EXCEL(\'excel_file\', A1)',
        tip: 'Downloads frame as excel file',
        icon: '',
        returns:
            {
                type: Type.Boolean,
                title: 'result',
                tip: 'Download result'
            }
    })
    @bind
    public async downloadAsExcel(
        @Param({
            title: 'api',
            tip: 'api',
            type: SheetType.SheetAPI
        }) api:ISheetAPI,
        @Param({
            title: 'filename',
            tip: 'Downloaded file name',
            type: Type.String
        }) filename:AString,
        @Param({
            title: 'frame',
            tip: 'Frame with Sheets to download',
            type: Type.Frame
        }) frame:AFrame,
        @Param({
            title: 'namesOnFirstRow',
            tip: 'Export column names as first row. Default is TRUE',
            type: Type.Boolean,
            optional: true
        }) namesOnFirstRow:ABoolean):Promise<ABoolean | AError>
    {
        try
        {
            const sheets:Array<any> = frame.getData().map((sheetRow:Array<IValue>) =>
            {
                const sheetFrame:IFrame = sheetRow[1] as IFrame;
                const sheetData:Array<any> = sheetFrame.getData();

                const exportSheetData:Array<Array<IExportCell>> = sheetData.map((sheetDataRow:Array<IValue>):Array<IExportCell> =>
                {
                    return sheetDataRow.map((sheetDataCell:IValue):IExportCell =>
                    {
                        if( Type.isInstanceOf(sheetDataCell, Type.Frame) )
                            throw new Error('#NESTED_DATA_NOT_SUPPORTED');

                        const isNumeric:boolean = Type.isInstanceOf(sheetDataCell, Type.Number) || Type.isInstanceOf(sheetDataCell, Type.Integer);

                        return {
                            value: isNumeric ? (sheetDataCell as ANumber).value : sheetDataCell.toString(),
                            type: isNumeric ? 'number' : 'string'
                        };
                    });
                });

                if( !namesOnFirstRow || namesOnFirstRow.value )
                    exportSheetData.splice(
                        0,
                        0,
                        sheetFrame.getFields().map(
                            (field:IFrameField) => ({value: field.name, type: 'string'})
                        )
                    );

                return {
                    name: sheetRow[0].toString(),
                    data: exportSheetData
                };
            });

            const config = {
                filename: filename.value,
                sheets
            };

            const result:boolean = await zipcelx(config);

            return result ? ABoolean.true() : AError.fromString('#INVALID_ARGUMENTS');
        }
        catch( e )
        {
            return AError.fromString('#NESTED_DATA_NOT_SUPPORTED');
        }
    }

    @Function({
        title: 'DOWNLOAD_AS_CSV(filename, data[, namesOnFirstRow])',
        alias: 'DOWNLOAD_AS_CSV',
        example: 'DOWNLOAD_AS_CSV(\'csv_file\', A1)',
        tip: 'Downloads frame or range as csv file',
        icon: '',
        returns:
            {
                type: Type.Boolean,
                title: 'result',
                tip: 'Download result'
            }
    })
    @bind
    public downloadAsCsv(
        @Param({
            title: 'api',
            tip: 'api',
            type: SheetType.SheetAPI
        }) api:ISheetAPI,
        @Param({
            title: 'filename',
            tip: 'Downloaded file name',
            type: Type.String
        }) filename:AString,
        @Param({
            title: 'data',
            tip: 'Frame or range to download',
            type: SheetType.Any
        }) data:any,
        @Param({
            title: 'namesOnFirstRow',
            tip: 'Export column names as first row. Default is TRUE',
            type: Type.Boolean,
            optional: true
        }) namesOnFirstRow:ABoolean):ABoolean | AError
    {
        let csv:string = '';
        let isRange:boolean = false;
        let frame:IFrame;

        if( SheetValueType.isInstanceOf(data, SheetType.SheetRange) )
        {
            frame = FormulaValueCoercionUtils.SheetRangeToIValue(api.getSheetContext(), new SimpleNamingContext(), data) as IFrame;
            isRange = true;
        }
        else if( SheetValueType.isInstanceOf(data, SheetType.SheetLocation) )
            frame = FormulaValueCoercionUtils.SheetLocationToIValue(api.getSheetContext(), new SimpleNamingContext(), data) as IFrame;
        else if( Type.isInstanceOf(data, Type.Frame) )
            frame = data as IFrame;

        if( frame && !frame.isError )
        {
            const sheetData:Array<any> = frame.getData();

            try
            {
                csv = sheetData.map((sheetDataRow:Array<IValue>):string =>
                {
                    return sheetDataRow.map((sheetDataCell:IValue):string =>
                    {
                        if( Type.isInstanceOf(sheetDataCell, Type.Frame) )
                            throw new Error('#NESTED_DATA_NOT_SUPPORTED');

                        return sheetDataCell.toString();
                    }).join(',');
                }).join('\n');
            }
            catch( e )
            {
                return AError.fromString('#NESTED_DATA_NOT_SUPPORTED');
            }

            if( !isRange && (!namesOnFirstRow || namesOnFirstRow.value) )
                csv = frame.getFields().map((field:IFrameField):string => field.name).join(',') + '\n' + csv;

            const blob = new Blob([csv], {type: 'text/plain;charset=utf-8'});

            FileSaver.saveAs(blob, `${filename.value}.csv`);

            return ABoolean.true();
        }

        return AError.fromString('#INVALID_ARGUMENTS');
    }
}
